import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Full-screen photo lightbox for appeal evidence (AUT-132). Mounts over
 * the moderation drawer via MUI `<Dialog fullScreen>` so all other
 * interactions blur — moderation is single-task.
 *
 * Mirrors the reports `Lightbox` (AUT-127) but takes a plain `photos`
 * URL list: appeal evidence photos carry no stable `id` / `category`
 * (the wire ref is `{ url, kind, expiresAt }`), so coercing the reports
 * `AdminReportMediaDto` shape would be wrong. Documents are NOT shown
 * here — they open in a new tab from their tile.
 *
 * Behaviour:
 *   - `<img>` only (no `<video>`; appeal evidence is `photo | document`).
 *   - Keyboard ←/→ cycles; ESC closes (MUI handles ESC, arrows at window).
 *   - `onPhotoError` fires on an `<img>` 403/404 (signed-URL expiry) so the
 *     parent can surface the re-fetch hint.
 */
export interface EvidenceLightboxProps {
  open: boolean;
  /** Signed photo URLs, in the same order as the on-strip photo tiles. */
  photos: string[];
  currentIndex: number;
  onClose: () => void;
  onIndexChange: (next: number) => void;
  /** Fired on an `<img>` load error (expired URL). */
  onPhotoError: () => void;
}

const NAV_BTN_SX = {
  color: '#fff',
  bgcolor: 'rgba(255,255,255,0.08)',
} as const;

/** Header overlay — close + index counter. */
const LightboxHeader = ({
  current,
  total,
  onClose,
}: {
  current: number;
  total: number;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <IconButton onClick={onClose} aria-label={t('appeals.evidence.close')} sx={NAV_BTN_SX}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 2 }}>
        <Typography sx={{ color: '#fff', fontFamily: 'monospace', fontSize: 14 }}>
          {`${current + 1} / ${total}`}
        </Typography>
      </Box>
    </>
  );
};

/** Prev/Next arrows — rendered only when there is more than one photo. */
const LightboxArrows = ({
  onPrev,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <>
      <IconButton
        onClick={onPrev}
        aria-label={t('appeals.evidence.prev')}
        sx={{
          ...NAV_BTN_SX,
          position: 'absolute',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        onClick={onNext}
        aria-label={t('appeals.evidence.next')}
        sx={{
          ...NAV_BTN_SX,
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </>
  );
};

export const EvidenceLightbox = ({
  open,
  photos,
  currentIndex,
  onClose,
  onIndexChange,
  onPhotoError,
}: EvidenceLightboxProps): ReactElement | null => {
  const goPrev = useCallback(() => {
    if (photos.length === 0) return;
    onIndexChange((currentIndex - 1 + photos.length) % photos.length);
  }, [currentIndex, photos.length, onIndexChange]);

  const goNext = useCallback(() => {
    if (photos.length === 0) return;
    onIndexChange((currentIndex + 1) % photos.length);
  }, [currentIndex, photos.length, onIndexChange]);

  // Window-level keyboard nav so the dialog doesn't need focus. Don't
  // hijack keys while the user types in a filter / field over the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, goPrev, goNext]);

  const src = photos[currentIndex];
  if (!src) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      slotProps={{ paper: { sx: { bgcolor: 'rgba(0,0,0,0.92)' } } }}
    >
      <LightboxHeader current={currentIndex} total={photos.length} onClose={onClose} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          position: 'relative',
        }}
      >
        <Box
          component="img"
          src={src}
          alt={`${currentIndex + 1} / ${photos.length}`}
          onError={onPhotoError}
          sx={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain' }}
        />
        {photos.length > 1 ? <LightboxArrows onPrev={goPrev} onNext={goNext} /> : null}
      </Box>
    </Dialog>
  );
};
